/**
 * AI Credits Store
 *
 * Valtio store for managing user AI credits
 */
import { proxy } from "valtio";
import { db, tx } from "@/lib/instantdb";

export type SubscriptionTier = "free" | "pro" | "enterprise";

export interface UserAISettings {
  id: string; // This IS the userId
  allowed_providers: string[];
  credits_remaining: number;
  credits_total: number;
  rate_limit_rpm: number;
  rate_limit_tpm: number;
  budget_limit: number;
  budget_used: number;
  budget_period_start: string;
  webhook_url?: string;
  last_alert_percent?: number;
  subscription_tier: SubscriptionTier;
  monthly_limit: number;
  credits_used_this_month: number;
  last_credit_reset: string;
}

interface AICreditsStore {
  // Edit dialog state
  editingUser: UserAISettings | null;
  editDialogOpen: boolean;

  // Action state
  actionLoading: boolean;
  actionError: string | null;

  // Search
  searchQuery: string;
}

export const aiCreditsStore = proxy<AICreditsStore>({
  editingUser: null,
  editDialogOpen: false,
  actionLoading: false,
  actionError: null,
  searchQuery: "",
});

// ========== Dialog Actions ==========

export function openEditDialog(user: UserAISettings) {
  aiCreditsStore.editingUser = user;
  aiCreditsStore.editDialogOpen = true;
}

export function closeEditDialog() {
  aiCreditsStore.editingUser = null;
  aiCreditsStore.editDialogOpen = false;
}

export function setSearchQuery(query: string) {
  aiCreditsStore.searchQuery = query;
}

// ========== CRUD Actions ==========

/**
 * Update user credits and settings
 */
export async function updateUserCredits(
  userId: string,
  updates: {
    credits_remaining?: number;
    monthly_limit?: number;
    subscription_tier?: SubscriptionTier;
  }
) {
  aiCreditsStore.actionLoading = true;
  aiCreditsStore.actionError = null;

  try {
    await db.transact(
      tx.user_ai_settings[userId].update(updates)
    );
    closeEditDialog();
  } catch (e) {
    aiCreditsStore.actionError =
      e instanceof Error ? e.message : "Failed to update user credits";
    throw e;
  } finally {
    aiCreditsStore.actionLoading = false;
  }
}

/**
 * Reset monthly usage for a user
 */
export async function resetMonthlyUsage(userId: string) {
  aiCreditsStore.actionLoading = true;
  aiCreditsStore.actionError = null;

  try {
    await db.transact(
      tx.user_ai_settings[userId].update({
        credits_used_this_month: 0,
        last_credit_reset: new Date().toISOString(),
      })
    );
  } catch (e) {
    aiCreditsStore.actionError =
      e instanceof Error ? e.message : "Failed to reset monthly usage";
    throw e;
  } finally {
    aiCreditsStore.actionLoading = false;
  }
}

/**
 * Add credits to a user's balance (top-up)
 */
export async function topUpCredits(userId: string, amount: number) {
  aiCreditsStore.actionLoading = true;
  aiCreditsStore.actionError = null;

  try {
    // Query current balance
    const { data } = await db.queryOnce({
      user_ai_settings: {
        $: { where: { id: userId } },
      },
    });

    const current = data?.user_ai_settings?.[0];
    if (!current) {
      throw new Error("User AI settings not found");
    }

    const newBalance = (current.credits_remaining as number) + amount;

    await db.transact(
      tx.user_ai_settings[userId].update({
        credits_remaining: newBalance,
      })
    );
  } catch (e) {
    aiCreditsStore.actionError =
      e instanceof Error ? e.message : "Failed to top up credits";
    throw e;
  } finally {
    aiCreditsStore.actionLoading = false;
  }
}

export function clearActionError() {
  aiCreditsStore.actionError = null;
}

// ========== Tier Defaults ==========

export const TIER_DEFAULTS: Record<
  SubscriptionTier,
  { monthly_limit: number; rate_limit_rpm: number; rate_limit_tpm: number }
> = {
  free: { monthly_limit: 10, rate_limit_rpm: 10, rate_limit_tpm: 10000 },
  pro: { monthly_limit: 100, rate_limit_rpm: 60, rate_limit_tpm: 100000 },
  enterprise: { monthly_limit: 1000, rate_limit_rpm: 300, rate_limit_tpm: 500000 },
};
